import { TIcon } from '@acceso/design-system';
import { FieldErrorsImpl, FieldValues } from 'react-hook-form';

import { TReviewStates } from '@/constants';
import { I18nextTranslationKeys } from '@/i18n/i18next';

export type Nullable<T> = T | null;

export type NullableString = Nullable<string>;
export type NullableNumber = Nullable<number>;
export type NullableDate = Nullable<Date>;

export type Strings = string[];
export type NullableStrings = Nullable<Strings>;

export type Numbers = number[];
export type NullableNumbers = Nullable<Numbers>;

export type UUID = string;
export type NullableUUID = Nullable<UUID>;
export type UUIDs = UUID[];
export type NullableUUIDs = Nullable<UUIDs>;

export interface MenuItem {
  name: string;
  icon: TIcon;
  permissions?: string[];
  routeName?: string;
}

export type Menu = {
  submenu: MenuItem[];
} & MenuItem;

export type Language = 'es' | 'ca' | 'eu' | 'gl' | 'en';
export type Locale = 'es_ES' | 'en_US' | 'ca_CA' | 'eu_EU';

export type LanguageDescription<TKey extends string = Locale> = Record<TKey, string>;

export interface Option<T = string> {
  label: string;
  value: T;
}

export interface ListBy {
  exclusion: UUIDs;
  inclusion: UUID[];
}

export interface RangeFilter<T> {
  gte: Nullable<T>;
  gt: Nullable<T>;
  lte: Nullable<T>;
  lt: Nullable<T>;
}

export type DatetimeFilter = RangeFilter<Date>;

export type DatetimeStringFilter = RangeFilter<string>;

export type NumberFilter = RangeFilter<number>;

export interface Bucket {
  key: number;
  min: number;
  max: number;
  total: number;
}

export type NumericRange = {
  min: number;
  max: number;
};

export type Buckets = Bucket[];

export type NullableBuckets = Nullable<Bucket[]>;

export type FormProps<FV extends FieldValues, T> = {
  values?: FV;
  defaultValues?: FV;
  onSuccess?: (data: T, reset: () => void) => void;
  onError?: (errors: Partial<FieldErrorsImpl<FV>>, reset: () => void) => void;
  onCancel?: (reset: () => void) => void;
};

export type DrawerProps<FV extends FieldValues, T> = FormProps<FV, T> & {
  isOpen: boolean;
};

type Prev = [never, 0, 1, 2, 3, 4, 5];
export type Path<T, Depth extends number = 5> = Depth extends 0
  ? never
  : T extends object
    ? {
        [K in keyof T & string]: T[K] extends object
          ? `${K}` | `${K}.${Path<T[K], Prev[Depth]>}`
          : `${K}`;
      }[keyof T & string]
    : never;

export type PathValue<T, P extends string> = P extends `${infer Key}.${infer Rest}`
  ? Key extends keyof T
    ? Rest extends PathValue<T[Key], Rest>
      ? T[Key]
      : never
    : never
  : P extends keyof T
    ? T[P]
    : never;

enum FormTypeEnum {
  CREATE = 'create',
  UPDATE = 'update',
  DUPLICATE = 'duplicate',
  ADD_TO = 'add_to',
  CREATE_CHILD = 'create_child',
}

type DrawerFormDataType<FormData> =
  | { type: FormTypeEnum.UPDATE | FormTypeEnum.DUPLICATE; data: FormData }
  | { type: FormTypeEnum.CREATE_CHILD | FormTypeEnum.ADD_TO; data?: null }
  | { type: FormTypeEnum.CREATE; data?: FormData | null };

enum BatchActionsEnum {
  DELETE = 'delete',
  UPDATE = 'update',
}

type DragSource = {
  index: number;
  droppableId: string;
};

type DragDestination = {
  index: number;
  droppableId: string;
};

type DragType = 'UNIT_GROUP' | 'UNIT';

type DragStart = {
  draggableId: string;
  type: string;
  source: DragSource;
};

type DragUpdate = DragStart & {
  destination?: DragDestination;
};

type DragResult = DragUpdate & {
  reason: 'DROP' | 'CANCEL';
};

type OrderData = {
  value: SortOrder;
  icon: TIcon;
};

type SortField =
  | 'publication_date'
  | 'title'
  | 'media.name'
  | 'economical_value'
  | 'audience'
  | 'delivered_at';

type SortOrder = 'asc' | 'desc';

type SortBy<T = SortField> = {
  field: T;
  order: SortOrder;
};

type TFilterProps = {
  onToggleFilter: () => void;
  isOpen: boolean;
  isDark?: boolean;
  showLabel?: boolean;
  isVerticalStyle?: boolean;
  onClose?: () => void;
  reviewStateOptions?: TReviewStates[];
  className?: string;
};

type TValueToLabels = {
  [key: string]: {
    label: string;
    tooltipHidden: I18nextTranslationKeys;
    tooltipVisible: I18nextTranslationKeys;
    icon: JSX.Element;
    onClick: () => void;
    disabled?: boolean;
  };
};

enum BroadcastScheduleProgramEvents {
  Review = 'R',
}

enum State {
  Active = 'active',
  Inactive = 'inactive',
}

type TRect = [number, number, number, number];

enum EReviewIncidenceType {
  OTHERS = 0,
  NOT_BROADCAST = 1,
  NOT_RECORDED = 2,
  poor_sound = 3,
  poor_image = 4,
  poor_sound_and_image = 5,
}

export type TableSelection<Item> = {
  all: boolean;
  selectedItems: Item[];
  unselectedItems: Item[];
};

export enum FileUploadState {
  PENDING = 'PENDING',
  IN_PROGRESS = 'IN_PROGRESS',
  FINISHED = 'FINISHED',
  ERROR = 'ERROR',
}

export type FileUploadProgess = {
  id: string;
  name: string;
  state: FileUploadState;
};

export {
  BatchActionsEnum,
  BroadcastScheduleProgramEvents,
  FormTypeEnum,
  State,
  EReviewIncidenceType,
};
export type {
  DragDestination,
  DragResult,
  DragSource,
  DragStart,
  DragType,
  DragUpdate,
  DrawerFormDataType,
  OrderData,
  SortBy,
  SortField,
  SortOrder,
  TFilterProps,
  TRect,
  TValueToLabels,
};
